import { throttle } from 'lodash/fp';
import { isMobile } from 'mobile-device-detect';
import { Container, Graphics, type ICanvas } from 'pixi.js-legacy';

import { OrderbookSide, findIndex, OrderbookMyOrdersIndicators } from '@exchange/libs/orderbook/service/src';

import DataPoint from './pixi-datapoint';
import Divider from './pixi-divider';
import Footer from './pixi-footer';
import Graph from './pixi-graph';
import HoverTooltips from './pixi-hover-tooltips';
import MyOrders from './pixi-my-orders';
import { DataPointModel, DepthChartPriceLevel, Zoom } from '../interfaces';

interface ChartSie {
  graph: Graph;
  arrayToDraw: Array<DepthChartPriceLevel>;
  storeArray: Array<DepthChartPriceLevel>;
  cachedArray: Array<DepthChartPriceLevel>;
  total: number;
}

export default class Chart extends Container {
  private fontName!: string;

  public graphics = new Graphics();

  public mouseDeltaY = 0;

  public mouseDataPoint = new DataPoint();

  public stepDataPoint = new DataPoint();

  public override interactive = false;

  public priceLabelWidth: number;

  public lastMatchedPrice!: number;

  public hoverTooltips: HoverTooltips;

  public divider: Divider;

  public footer: Footer;

  public myOrders: MyOrders;

  public footerHeight = 48; // px

  public isMobile = isMobile;

  private cHeight = 0;

  private cWidth = 0;

  public asks: ChartSie = {
    graph: {} as Graph,
    arrayToDraw: [],
    storeArray: [],
    cachedArray: [],
    total: 0,
  };

  public bids: ChartSie = {
    graph: {} as Graph,
    arrayToDraw: [],
    storeArray: [],
    cachedArray: [],
    total: 0,
  };

  public zoom: Zoom;

  public getFormattedNumber: (value: number, isMarketPrecision?: boolean) => string;

  public orderFormClick: ([askPosition, bidPosition]: [DataPointModel, DataPointModel]) => void;

  get graphHeight() {
    return Math.ceil((this.cHeight - this.footerHeight) / 2);
  }

  public setup({ askData, bidData, fontName }: { askData: Array<DepthChartPriceLevel>; bidData: Array<DepthChartPriceLevel>; fontName: string }) {
    const width = this.cWidth;
    const height = Math.ceil(this.cHeight - this.footerHeight);
    const sideHeight = height / 2;

    const priceLabelWidth = this.cWidth / 2.5;
    const graphWidth = width - priceLabelWidth;
    const { graphHeight } = this;

    const footer = new Footer(fontName, this.mouseDataPoint, width, this.footerHeight, height, this.getFormattedNumber);
    const myOrders = new MyOrders({ dotX: width - 4, maxY: height });
    const hoverTooltips = new HoverTooltips({
      fontName,
      mouseDataPoint: this.mouseDataPoint,
      stepDataPoint: this.stepDataPoint,
      parentWidth: width,
      sideHeight,
      getFormattedNumber: this.getFormattedNumber,
    });
    const divider = new Divider({ fontName, sideHeight, getFormattedNumber: this.getFormattedNumber });

    const asks = new Graph({
      x: priceLabelWidth,
      y: 0,
      width: graphWidth,
      height: graphHeight,
      isAsk: true,
      arrayToDraw: askData,
      lastMatchedPrice: this.lastMatchedPrice,
    });
    const bids = new Graph({
      x: priceLabelWidth,
      y: height / 2,
      width: graphWidth,
      height: graphHeight,
      isAsk: false,
      arrayToDraw: bidData,
      lastMatchedPrice: this.lastMatchedPrice,
    });

    return {
      asks,
      bids,
      divider,
      footer,
      hoverTooltips,
      myOrders,
      priceLabelWidth,
    };
  }

  constructor({
    canvas,
    zoom,
    orderFormClick,
    getFormattedNumber,
    askData,
    bidData,
    cHeight,
    cWidth,
    fontName,
  }: {
    canvas: ICanvas;
    zoom: Zoom;
    orderFormClick: ([askPosition, bidPosition]: [DataPointModel, DataPointModel]) => void;
    getFormattedNumber: (value: number, isMarketPrecision?: boolean) => string;
    askData: Array<DepthChartPriceLevel>;
    bidData: Array<DepthChartPriceLevel>;
    cHeight: number;
    cWidth: number;
    fontName: string;
  }) {
    super();
    this.cHeight = cHeight;
    this.cWidth = cWidth;
    this.asks.arrayToDraw = askData;
    this.bids.arrayToDraw = bidData;
    this.zoom = zoom;
    this.fontName = fontName;
    this.orderFormClick = orderFormClick;
    this.getFormattedNumber = getFormattedNumber;

    const { asks, bids, divider, footer, hoverTooltips, myOrders, priceLabelWidth } = this.setup({
      askData,
      bidData,
      fontName,
    });

    this.footer = footer;
    this.hoverTooltips = hoverTooltips;
    this.myOrders = myOrders;
    this.divider = divider;
    this.asks.graph = asks;
    this.bids.graph = bids;
    this.priceLabelWidth = priceLabelWidth;

    this.addChild(this.graphics, this.hoverTooltips, this.footer, this.divider);
    this.handleEventListeners(canvas);
  }

  public refresh({
    askData,
    bidData,
    askTotal,
    bidTotal,
    lastMatchedPrice,
    zoomWindow,
    myOrders,
  }: {
    askData: Array<DepthChartPriceLevel>;
    bidData: Array<DepthChartPriceLevel>;
    askTotal: number;
    bidTotal: number;
    lastMatchedPrice: number;
    zoomWindow: number;
    myOrders: OrderbookMyOrdersIndicators;
  }) {
    if (!this.mouseDataPoint.isInsideCanvas) {
      this.zoom.window = zoomWindow;
    }

    this.asks.arrayToDraw = askData;
    this.bids.arrayToDraw = bidData;

    this.asks.total = askTotal;
    this.bids.total = bidTotal;

    this.lastMatchedPrice = lastMatchedPrice;

    this.myOrders.orders = this.myOrdersToY(myOrders);
    this.footer.visible = !Number.isNaN(lastMatchedPrice);

    this.footer.update();
    this.update();
  }

  public clear() {
    this.graphics.clear();
  }

  public resize({ cHeight, cWidth }: { cHeight: number; cWidth: number }) {
    this.removeChild(this.graphics, this.hoverTooltips, this.footer, this.divider);

    this.cHeight = cHeight;
    this.cWidth = cWidth;

    const { asks, bids, divider, footer, hoverTooltips, myOrders, priceLabelWidth } = this.setup({
      askData: this.asks.arrayToDraw,
      bidData: this.bids.arrayToDraw,
      fontName: this.fontName,
    });

    this.footer = footer;
    this.hoverTooltips = hoverTooltips;
    this.myOrders = myOrders;
    this.divider = divider;
    this.asks.graph = asks;
    this.bids.graph = bids;
    this.priceLabelWidth = priceLabelWidth;

    this.addChild(this.graphics, this.hoverTooltips, this.footer, this.divider);

    this.update();
  }

  public update() {
    this.graphics.clear();

    this.asks.graph.update(this.asks.arrayToDraw, this.lastMatchedPrice);
    this.bids.graph.update(this.bids.arrayToDraw, this.lastMatchedPrice);
    this.divider.update(this.lastMatchedPrice);

    const priceWindow = (this.lastMatchedPrice / 100) * this.zoom.window;
    const sizeWindow = Math.max(this.asks.graph.getMaxSize(priceWindow), this.bids.graph.getMaxSize(priceWindow));

    this.asks.graph.setZoom(sizeWindow, priceWindow);
    this.bids.graph.setZoom(sizeWindow, priceWindow);

    this.asks.graph.draw(this.graphics);
    this.bids.graph.draw(this.graphics);
    this.myOrders.draw(this.graphics);

    const point = this.convertPositionToDataPoint(this.mouseDeltaY);

    if (point) {
      this.mouseDataPoint.update(point);
    }

    const lsiASK = this.asks.graph.largestSizeItem;
    const lsiBID = this.bids.graph.largestSizeItem;

    this.stepDataPoint.update([this.convertPriceLevelToDataPoint({ priceLevel: lsiASK, isAsk: true }), this.convertPriceLevelToDataPoint({ priceLevel: lsiBID, isAsk: false })]);

    const lviASK = this.asks.graph.lastVisibleItem;
    const lviBID = this.bids.graph.lastVisibleItem;

    this.mouseDataPoint.updateLastVisible([
      this.convertPriceLevelToDataPoint({ priceLevel: lviASK, isAsk: true }),
      this.convertPriceLevelToDataPoint({ priceLevel: lviBID, isAsk: false }),
    ]);

    this.hoverTooltips.update();
  }

  private calculateDifference(newNum: number, originalNum: number) {
    return Math.abs(((newNum - originalNum) / originalNum) * 100);
  }

  private convertPositionToDataPoint(offsetY: number): undefined | [DataPointModel, DataPointModel] {
    const isAsk = offsetY < this.graphHeight;
    const side = isAsk ? this.asks : this.bids;
    const otherSide = isAsk ? this.bids : this.asks;
    const mid = this.lastMatchedPrice;

    if (!side || !side.graph || !otherSide || !otherSide.graph) {
      return undefined;
    }

    const position: DataPointModel = {
      coordinates: { x: 0, y: 0 },
      hoveredItem: { index: 0, exists: false },
      price: 0,
      priceToDisplay: 0,
      size: 0,
      sumTotal: 0,
      priceDiffPercent: 0,
      isAskHovered: isAsk,
    };

    position.price = side.graph.yToPrice(offsetY);
    position.hoveredItem = findIndex(position.price, side.arrayToDraw, isAsk ? OrderbookSide.ASK : OrderbookSide.BID);

    if (position.hoveredItem.index < 0) {
      position.size = 0;
    } else {
      let sum = 0;

      if (position.hoveredItem.index >= side.arrayToDraw.length) {
        ({ sum } = side.arrayToDraw[side.arrayToDraw.length - 1] || { sum: 0 });
      } else if (position.hoveredItem.index > 0 && !position.hoveredItem.exists) {
        ({ sum } = side.arrayToDraw[position.hoveredItem.index - 1] || { sum: 0 });
      } else {
        ({ sum } = side.arrayToDraw[position.hoveredItem.index] || { sum: 0 });
      }

      position.size = sum;
    }

    position.coordinates.y = side.graph.priceToY(position.price);
    position.coordinates.x = side.graph.sizeToX(position.size);

    position.priceDiffPercent = this.calculateDifference(position.price, mid);

    // OTHER SIDE
    let otherPrice = 0;
    let otherSize = 0;

    if (isAsk) {
      otherPrice = mid - mid * (position.priceDiffPercent / 100);
    } else {
      otherPrice = mid + mid * (position.priceDiffPercent / 100);
    }

    const otherItem = findIndex(otherPrice, otherSide.arrayToDraw, isAsk ? OrderbookSide.BID : OrderbookSide.ASK);

    if (otherItem.index < 0) {
      otherSize = 0;
    } else {
      let sum = 0;

      if (otherItem.index >= otherSide.arrayToDraw.length) {
        ({ sum } = otherSide.arrayToDraw[otherSide.arrayToDraw.length - 1] || { sum: 0 });
      } else if (otherItem.index > 0 && !otherItem.exists) {
        ({ sum } = otherSide.arrayToDraw[otherItem.index - 1] || { sum: 0 });
      } else {
        ({ sum } = otherSide.arrayToDraw[otherItem.index] || { sum: 0 });
      }

      otherSize = sum;
    }

    const otherPosition: DataPointModel = {
      coordinates: { x: otherSide.graph.sizeToX(otherSize), y: otherSide.graph.priceToY(otherPrice) },
      hoveredItem: otherItem,
      price: otherPrice,
      priceToDisplay: this.getFormattedNumber(otherPrice),
      size: otherSize,
      sumTotal: 0,
      priceDiffPercent: position.priceDiffPercent,
      isAskHovered: isAsk,
    };

    position.priceToDisplay = this.getFormattedNumber(position.price);

    return isAsk ? [position, otherPosition] : [otherPosition, position];
  }

  private convertPriceLevelToDataPoint({ isAsk, priceLevel }: { isAsk: boolean; priceLevel?: DepthChartPriceLevel }) {
    const side = isAsk ? this.asks : this.bids;
    const position: DataPointModel = {
      coordinates: { x: 0, y: 0 },
      hoveredItem: { index: 0, exists: false },
      price: 1,
      priceToDisplay: this.getFormattedNumber(1),
      size: 1,
      sumTotal: 0,
      priceDiffPercent: 0,
      isAskHovered: isAsk,
    };

    if (!priceLevel) {
      return position;
    }

    position.price = priceLevel.price;
    position.priceToDisplay = this.getFormattedNumber(priceLevel.price);
    position.size = priceLevel.amount;

    const ftd = side.arrayToDraw[0];

    if ((ftd && position.price > ftd.price && !isAsk) || (ftd && position.price < ftd.price && isAsk)) {
      position.price = ftd.price;
      position.priceToDisplay = this.getFormattedNumber(position.price);
    }

    position.hoveredItem = findIndex(+position.price, side.arrayToDraw, isAsk ? OrderbookSide.ASK : OrderbookSide.BID);

    if (position.hoveredItem.index < 0) {
      position.size = 0;
    } else {
      let sum = 0;

      if (position.hoveredItem.index >= side.arrayToDraw.length) {
        ({ sum } = side.arrayToDraw[side.arrayToDraw.length - 1] || { sum: 0 });
      } else if (position.hoveredItem.index > 0 && !position.hoveredItem.exists) {
        ({ sum } = side.arrayToDraw[position.hoveredItem.index - 1] || { sum: 0 });
      } else {
        ({ sum } = side.arrayToDraw[position.hoveredItem.index] || { sum: 0 });
      }

      position.size = sum;
    }

    if (side.graph) {
      position.coordinates.y = side.graph.priceToY(+position.price) + 1; // +1px to align perfectly with graph
      position.coordinates.x = side.graph.sizeToX(position.size);
    }

    position.priceToDisplay = this.getFormattedNumber(position.price);

    return position;
  }

  private myOrdersToY(orders: OrderbookMyOrdersIndicators) {
    const openOrders: Array<[number, number]> = [];
    const stopOrders: Array<[number, number]> = [];

    orders.orders.forEach((order) => {
      const price = order.originalPrice;

      openOrders.push([price, price > this.lastMatchedPrice ? this.asks.graph.priceToY(price) : this.bids.graph.priceToY(price)]);
    });

    orders.triggers.forEach((order) => {
      const price = order.originalPrice;

      stopOrders.push([price, price > this.lastMatchedPrice ? this.asks.graph.priceToY(price) : this.bids.graph.priceToY(price)]);
    });

    return { openOrders, stopOrders };
  }

  private handleEventListeners(canvas) {
    const throttleAtFPS = 15;

    // Pinch to zoom handler for mobile
    let scaling = false;
    let currentPinch: number;

    canvas.addEventListener(
      'pointerenter',
      throttle(throttleAtFPS, () => {
        this.mouseDataPoint.insideCanvas(true);
      }),
    );
    canvas.addEventListener(
      'pointerleave',
      throttle(throttleAtFPS, () => {
        scaling = false;
        this.mouseDataPoint.insideCanvas(false);
      }),
    );

    canvas.addEventListener(
      'wheel',
      throttle(throttleAtFPS, (e) => {
        e.preventDefault();
        const delta = e.deltaY * 0.01;

        if (this.zoom.window + delta >= this.zoom.min && this.zoom.window + delta <= this.zoom.max) {
          this.zoom.window = +(this.zoom.window + delta).toFixed(2);
        }
      }),
    );

    canvas.addEventListener(
      'touchstart',
      throttle(throttleAtFPS, (e) => {
        const { touches } = e;

        if (touches.length === 2 && this.isMobile) {
          this.mouseDataPoint.insideCanvas(false);
          scaling = true;
          currentPinch = Math.hypot(
            // @ts-ignore TODO fix it
            touches[0].pageX - touches[1].pageX,
            // @ts-ignore TODO fix it
            touches[0].pageY - touches[1].pageY,
          );
        }
      }),
    );

    canvas.addEventListener(
      'touchmove',
      throttle(throttleAtFPS, (e) => {
        const { touches } = e;

        if (scaling && touches.length === 2) {
          this.mouseDataPoint.insideCanvas(false);

          const newPinch = Math.hypot(
            // @ts-ignore TODO fix it
            touches[0].pageX - touches[1].pageX,
            // @ts-ignore TODO fix it
            touches[0].pageY - touches[1].pageY,
          );
          const delta = Math.sign(currentPinch - newPinch) / 10;

          if (this.zoom.window + delta >= this.zoom.min && this.zoom.window + delta <= this.zoom.max) {
            this.zoom.window = +(this.zoom.window + delta).toFixed(2);
          }

          currentPinch = newPinch;
        }
      }),
    );

    // Pointer handler
    canvas.addEventListener(
      'pointerdown',
      throttle(throttleAtFPS, (e) => {
        this.mouseDeltaY = e.offsetY;
        const points = this.convertPositionToDataPoint(e.offsetY);

        if (points) {
          this.orderFormClick(points);
        }
      }),
    );

    canvas.addEventListener(
      'pointermove',
      throttle(throttleAtFPS, (e) => {
        if (e.offsetY !== 0) {
          this.mouseDeltaY = e.offsetY;
          this.footer.update();
        }

        if (e.pressure) {
          const points = this.convertPositionToDataPoint(this.mouseDeltaY);

          if (points) {
            this.orderFormClick(points);
          }
        }
      }),
    );

    canvas.addEventListener(
      'pointerup',
      throttle(throttleAtFPS, (e) => {
        const points = this.convertPositionToDataPoint(e.offsetY);

        if (points) {
          this.orderFormClick(points);
        }

        scaling = false;
      }),
    );
  }
}
